// ═══════════════════════════════════════════════════════════════════════════════
// ACTIVITY LOGGER
// ═══════════════════════════════════════════════════════════════════════════════
// Writes to the `activity_log` table every time a meaningful action happens.
// Called from SourcingView (LOI sent, property deleted, contact added),
// App.js / DealRoom (deal created, stage changed), etc.
//
// Also tracks user logins in `user_sessions` so the team roster stays
// up-to-date automatically as new people sign in.

import { supabase } from "../supabase";

/**
 * Log an activity.
 * @param {object} opts
 * @param {string} opts.userName — display name of the actor
 * @param {string} opts.userEmail — email of the actor
 * @param {string} opts.action — machine key: 'loi_sent', 'deal_created', etc.
 * @param {string} opts.description — human-readable summary
 * @param {string} [opts.entityType] — 'deal', 'property', 'outreach', 'comp', 'contact'
 * @param {string} [opts.entityId] — UUID of the related row
 * @param {object} [opts.metadata] — extra context (old_stage, new_stage, amount, etc.)
 */
export async function logActivity({ userName, userEmail, action, description, entityType, entityId, metadata }) {
  try {
    const { error } = await supabase.from("activity_log").insert({
      user_name: userName || "Unknown",
      user_email: userEmail || null,
      action,
      description,
      entity_type: entityType || null,
      entity_id: entityId || null,
      metadata: metadata || {},
    });
    if (error) console.error("Activity log insert error:", error.message, error.details);
  } catch (e) {
    console.error("Activity log write failed:", e.message);
  }
}

/**
 * Record or update a user login in `user_sessions`.
 * Called once per app mount (in App.js after auth).
 */
export async function trackUserLogin({ email, displayName, jobTitle }) {
  if (!email) return;
  try {
    const { data: existing } = await supabase
      .from("user_sessions")
      .select("id, login_count")
      .eq("email", email)
      .limit(1);

    if (existing && existing.length > 0) {
      await supabase.from("user_sessions").update({
        display_name: displayName || undefined,
        job_title: jobTitle || undefined,
        last_login: new Date().toISOString(),
        login_count: (existing[0].login_count || 0) + 1,
      }).eq("id", existing[0].id);
    } else {
      await supabase.from("user_sessions").insert({
        email,
        display_name: displayName || null,
        job_title: jobTitle || null,
      });
    }
  } catch (e) {
    console.warn("User session tracking failed:", e.message);
  }
}

/**
 * Fetch the team roster (all users who have ever logged in).
 */
export async function loadTeamRoster() {
  const { data, error } = await supabase
    .from("user_sessions")
    .select("*")
    .order("display_name", { ascending: true });
  if (error) { console.warn("loadTeamRoster:", error.message); return []; }
  return data || [];
}

/**
 * Fetch recent activity entries.
 * @param {number} limit — how many to fetch (default 50)
 */
export async function loadRecentActivity(limit = 50) {
  const { data, error } = await supabase
    .from("activity_log")
    .select("*")
    .order("created_at", { ascending: false })
    .limit(limit);
  if (error) { console.warn("loadRecentActivity:", error.message); return []; }
  return data || [];
}

/**
 * Fetch tasks for a user (or all if no email specified).
 */
export async function loadTasks(email = null) {
  let q = supabase.from("tasks").select("*").order("created_at", { ascending: false });
  if (email) q = q.eq("assigned_to", email);
  const { data, error } = await q;
  if (error) { console.warn("loadTasks:", error.message); return []; }
  return data || [];
}

/**
 * Create a new task.
 */
export async function createTask(task) {
  const { data, error } = await supabase.from("tasks").insert(task).select().single();
  if (error) throw error;
  return data;
}

/**
 * Update a task (progress, status, etc.)
 */
export async function updateTask(id, updates) {
  const { data, error } = await supabase
    .from("tasks")
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq("id", id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

/**
 * Delete a task permanently.
 */
export async function deleteTask(id) {
  const { error } = await supabase.from("tasks").delete().eq("id", id);
  if (error) throw error;
}

/**
 * Auto-increment progress on active tasks for a user when a relevant action
 * occurs. Matches task titles containing action-specific keywords.
 *
 * Called after LOI sends, deal creations, property adds, etc.
 * The task title is the source of truth: if it mentions "LOI" and the action
 * is 'loi_sent', the counter goes up. This keeps it flexible — you can
 * create a task "Send 50 LOIs this week" or "Add 20 comps" and it just works.
 *
 * @param {string} userEmail — who performed the action
 * @param {string} action — the activity type ('loi_sent', 'deal_created', etc.)
 * @param {number} [increment=1] — how much to add
 */
export async function autoIncrementTasks(userEmail, action, increment = 1) {
  if (!userEmail) return;
  try {
    const { data: activeTasks } = await supabase
      .from("tasks")
      .select("id, title, current_count, target_count")
      .eq("assigned_to", userEmail)
      .eq("status", "active");
    if (!activeTasks || activeTasks.length === 0) return;

    // Keyword map: which actions match which task-title keywords
    const ACTION_KEYWORDS = {
      loi_sent: ["loi", "offer", "outreach", "send"],
      deal_created: ["deal", "pipeline", "add deal"],
      comp_added: ["comp", "lease comp", "sale comp"],
      property_added: ["property", "sourcing", "upload", "costar"],
      contact_added: ["contact"],
      stage_changed: ["stage", "move"],
    };

    const keywords = ACTION_KEYWORDS[action] || [];
    if (keywords.length === 0) return;

    for (const task of activeTasks) {
      const titleLower = (task.title || "").toLowerCase();
      const matches = keywords.some(kw => titleLower.includes(kw));
      if (matches && task.target_count) {
        const newCount = Math.min((task.current_count || 0) + increment, task.target_count);
        await supabase.from("tasks").update({
          current_count: newCount,
          updated_at: new Date().toISOString(),
          status: newCount >= task.target_count ? "completed" : "active",
        }).eq("id", task.id);
      }
    }
  } catch (e) {
    console.warn("autoIncrementTasks failed:", e.message);
  }
}
